import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Printer } from "lucide-react";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DocumentPreviewModal from "./DocumentPreviewModal";
import printPortraitFrame from "../../../utils/print/printPortraitFrame";
import { isPreferenceEnabled, useDocumentPreferences } from "../../../utils/documentPreferences";
import {
  buildDocumentPrintModel,
  documentFileName,
  fetchDocumentPrintContext,
  waitForImages,
} from "./documentPrintHelpers";

const findSafeCanvasBreak = (canvas, context, sourceY, proposedEnd) => {
  if (proposedEnd >= canvas.height) return canvas.height;

  // Look back through roughly 16 mm of the captured page for the latest row
  // with the least ink. This keeps paragraph lines and table text from being
  // cut in half while retaining every source pixel exactly once.
  const searchDepth = Math.min(120, proposedEnd - sourceY);
  const searchTop = proposedEnd - searchDepth;
  const insetX = Math.floor(canvas.width * 0.04);
  const scanWidth = Math.max(1, canvas.width - insetX * 2);
  const pixels = context.getImageData(insetX, searchTop, scanWidth, searchDepth).data;
  let bestRow = searchDepth - 1;
  let bestInk = Number.POSITIVE_INFINITY;

  for (let row = 0; row < searchDepth; row += 1) {
    let ink = 0;
    for (let column = 0; column < scanWidth; column += 4) {
      const offset = (row * scanWidth + column) * 4;
      const alpha = pixels[offset + 3];
      if (
        alpha > 10 &&
        (pixels[offset] < 245 || pixels[offset + 1] < 245 || pixels[offset + 2] < 245)
      ) {
        ink += 1;
      }
    }
    // On equal whitespace scores, prefer the later row to use more of the page.
    if (ink <= bestInk) {
      bestInk = ink;
      bestRow = row;
    }
  }

  return searchTop + bestRow + 1;
};

const DocumentPrintActions = ({
  row,
  config,
  documentType,
  documentModel,
  variant = "buttons",
  buttonClassName = "",
  buttonStyle,
  label = "Print / PDF",
  onError,
}) => {
  const printRef = useRef(null);
  const [printDocument, setPrintDocument] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const documentPreferences = useDocumentPreferences();

  const applyPreferences = (model) => documentPreferences === null ? model : {
    ...model,
    showDiscount: isPreferenceEnabled(documentPreferences.discountCheck),
    showHsn: isPreferenceEnabled(documentPreferences.hsnCheck),
  };

  const reportError = (error) => {
    const message = error?.message || error || "Unable to generate document";
    if (onError) onError(message);
    else window.alert(message);
  };

  const buildModel = async () => {
    if (documentModel) {
      return applyPreferences(buildDocumentPrintModel({
        existingModel: documentModel,
        documentType: documentType || config?.documentType || "invoice",
        config,
      }));
    }
    const resolvedDocumentType = documentType || config?.documentType || "invoice";
    const context = await fetchDocumentPrintContext({
      ...(row || {}),
      documentType: resolvedDocumentType,
    });
    return applyPreferences(buildDocumentPrintModel({
      row,
      documentType: resolvedDocumentType,
      config,
      context,
    }));
  };

  const openPreview = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const nextDocument = await buildModel();
      setPrintDocument(nextDocument);
      setOpen(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    print: printPortraitFrame,
    documentTitle: printDocument?.meta?.number || printDocument?.title || "Document",
    removeAfterPrint: true,
    onBeforeGetContent: () => waitForImages(printRef.current),
    pageStyle: `
      @page document-portrait { size: A4 portrait; margin: 12mm; }
      @media print {
        html, body { width: auto !important; min-width: 0 !important; height: auto !important; min-height: 0 !important; margin: 0 !important; overflow: visible !important; background: #ffffff !important; }
        .no-print { display: none !important; }
        .document-print-area {
          page: document-portrait;
          width: 186mm !important;
          max-width: 186mm !important;
          min-height: auto !important;
          margin: 0 auto !important;
          padding: 2mm !important;
          border: 1px solid #a8a8a8 !important;
          box-sizing: border-box !important;
          box-shadow: none !important;
          -webkit-box-decoration-break: clone;
          box-decoration-break: clone;
        }
        table { page-break-inside: auto; }
        tr { page-break-inside: avoid; page-break-after: auto; }
        thead { display: table-header-group; }
      }
    `,
  });

  const handleDownload = async () => {
    if (!printRef.current || downloading) return;
    setDownloading(true);
    try {
      await waitForImages(printRef.current);
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        windowWidth: printRef.current.scrollWidth,
        windowHeight: printRef.current.scrollHeight,
      });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = 210;
      const pageHeight = 297;
      const pageMargin = 12;
      const pagePadding = 2;
      const frameWidth = pageWidth - pageMargin * 2;
      const frameHeight = pageHeight - pageMargin * 2;
      const imageWidth = frameWidth - pagePadding * 2;
      const imagePageHeight = frameHeight - pagePadding * 2;
      const pixelsPerMm = canvas.width / imageWidth;
      const pixelsPerPage = Math.max(1, Math.floor(imagePageHeight * pixelsPerMm));
      const sourceContext = canvas.getContext("2d");
      let sourceY = 0;
      let pageIndex = 0;

      // Crop the captured document into real page-sized images. Reusing one
      // tall image with a negative Y offset causes every page to overlap by
      // the PDF margin and repeats content at page boundaries.
      while (sourceY < canvas.height) {
        const proposedEnd = Math.min(sourceY + pixelsPerPage, canvas.height);
        const sourceEnd = findSafeCanvasBreak(
          canvas,
          sourceContext,
          sourceY,
          proposedEnd,
        );
        const sliceHeight = sourceEnd - sourceY;
        const pageCanvas = window.document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;
        const pageContext = pageCanvas.getContext("2d");
        pageContext.fillStyle = "#ffffff";
        pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageContext.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight,
        );

        if (pageIndex > 0) pdf.addPage();
        const renderedHeight = sliceHeight / pixelsPerMm;
        pdf.addImage(
          pageCanvas.toDataURL("image/png", 1.0),
          "PNG",
          pageMargin + pagePadding,
          pageMargin + pagePadding,
          imageWidth,
          renderedHeight,
          undefined,
          "FAST",
        );
        pdf.setDrawColor(168, 168, 168);
        pdf.setLineWidth(0.25);
        pdf.rect(pageMargin, pageMargin, frameWidth, frameHeight);

        sourceY = sourceEnd;
        pageIndex += 1;
      }

      pdf.save(documentFileName(printDocument));
    } catch (error) {
      reportError(error);
    } finally {
      setDownloading(false);
    }
  };

  const modal = open ? (
    <DocumentPreviewModal
      document={printDocument}
      open={open}
      onClose={() => setOpen(false)}
      onPrint={handlePrint}
      onDownload={handleDownload}
      downloading={downloading}
      printRef={printRef}
    />
  ) : null;

  if (variant === "menu") {
    return (
      <>
        <button
          type="button"
          className={buttonClassName}
          style={buttonStyle}
          onClick={openPreview}
          disabled={loading}
        >
          <Download size={16} />
          {loading ? "Preparing..." : label}
        </button>
        {modal && createPortal(modal, window.document.body)}
      </>
    );
  }

  return (
    <>
      <span className="document-print-action-buttons no-print">
        <button onClick={openPreview} disabled={loading}>
          <Download size={16} /> {loading ? "Preparing..." : "Download PDF"}
        </button>
        <button onClick={openPreview} disabled={loading}>
          <Printer size={16} /> Print
        </button>
      </span>
      {modal && createPortal(modal, window.document.body)}
    </>
  );
};

export default DocumentPrintActions;
